import TextButton from 'components/Luxkit/Button/TextButton'
import Group from 'components/utils/Group'
import Heading from 'components/Luxkit/Typography/Heading'
import React, { FormEventHandler, useCallback, useContext, useEffect, useState } from 'react'
import BusinessTravellerStepper from 'businessTraveller/components/stepper/BusinessTravellerStepGroup'
import BodyText from 'components/Luxkit/Typography/BodyText'
import TextInput from 'components/Common/Form/Input/TextInput'
import styled from 'styled-components'
import { rem } from 'polished'
import { mediaQueryUp } from 'components/utils/breakpoint'
import HiddenInput from 'components/Common/Form/Input/HiddenInput'
import { getData as getCountryData } from 'country-list'
import Select from 'components/Common/Form/Input/Select'
import BUSINESS_TRAVELLER_BUSINESS_INDUSTRIES_OPTIONS from 'businessTraveller/constants/businessTravellerBusinessIndustries'
import BUSINESS_TRAVELLER_BUSINESS_EMPLOYEE_COUNT_OPTIONS from 'businessTraveller/constants/businessTravellerBusinessEmployeeCount'
import Hr from 'components/Common/Hr'
import PhoneInput from 'components/Common/Form/Input/PhoneInput'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import { useMutation } from '@tanstack/react-query'
import registerBusiness from 'businessTraveller/api/registerBusiness'
import { formToObject } from 'lib/forms/formToObject'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { updateUserDetails } from 'actions/AuthActions'
import { pushWithRegion } from 'actions/NavigationActions'
import { BUSINESS_TRAVELLER_BUSINESS_SIGNUP_COMPLETE_ROUTE } from 'businessTraveller/routes/businessTravellerRoutes'
import { BusinessTravellerBusinessOnboardingStateContext } from 'businessTraveller/contexts/BusinessTravellerBusinessOnboardingContexts'
import LayoutContainer from 'components/Common/LayoutContainer'
import { showSnackbar } from 'components/Luxkit/Snackbar/AppSnackbar'
import fetchAccountBusiness from 'actions/businessTraveller/fetchBusinessAccount'

const SignupContainer = styled(LayoutContainer)`
  background-color:  ${props => props.theme.palette.neutral.default.eight};
  padding: ${rem(20)};

  ${mediaQueryUp.tablet} {
    padding-top: ${rem(24)};
  }
`

const countryNames = getCountryData().map(country => country.name).sort()

const CreateBusinessForm = styled.form`
  position: relative;
`

const InputGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${rem(12)};

  ${mediaQueryUp.tablet} {
    grid-template-columns: repeat(2, 1fr);
  }
`

const stepStatuses: App.BusinessTraveller.BusinessSignupStepKeyStatuses = {
  validate_email: 'complete',
  create_customer: 'complete',
  create_business: 'active',
}

interface Props {
  steps: App.BusinessTraveller.BusinessSignupSteps
}

function BusinessTravellerSignupCreateBusiness(props: Props) {
  const { steps } = props
  const [isFormSubmitting, setIsFormSubmitting] = useState<boolean>(false)

  const dispatch = useAppDispatch()
  const { businessName } = useContext(BusinessTravellerBusinessOnboardingStateContext)

  const businessRegistrationMutation = useMutation({
    mutationFn: registerBusiness,
    onSuccess: (data, businessData) => {
      dispatch(fetchAccountBusiness())

      // Trigger an update of the customer account
      dispatch(updateUserDetails({
        givenName: businessData.adminDetails.firstName,
        surname: businessData.adminDetails.lastName,
        phone: businessData.businessDetails.phone.phone,
        phonePrefix: businessData.businessDetails.phone.prefix,
      }))

      setIsFormSubmitting(false)
    },
    onError: (error: App.ApiCallFailure) => {
      showSnackbar(
        error.message,
        'critical',
        { heading: 'Error submitting business' },
      )

      setIsFormSubmitting(false)
    },
  })

  const handleSubmission = useCallback<FormEventHandler<HTMLFormElement>>((e) => {
    e.preventDefault()
    setIsFormSubmitting(true)
    const formData = formToObject<App.BusinessTraveller.BusinessSignupFormData>(e.currentTarget)
    businessRegistrationMutation.mutate(formData)
  }, [businessRegistrationMutation])

  const business = useAppSelector((state) => state.businessTraveller.currentBusinessAccount?.business)

  useEffect(() => {
    if (business) {
      dispatch(pushWithRegion(BUSINESS_TRAVELLER_BUSINESS_SIGNUP_COMPLETE_ROUTE))
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [business])

  return (
    <SignupContainer>
      <CreateBusinessForm onSubmit={handleSubmission} >
        {isFormSubmitting && <LoadingIndicator floating />}
        <Group direction="vertical" gap={20}>
          <Heading variant="heading3">Complete your account details</Heading>
          <BusinessTravellerStepper steps={steps} stepStatuses={stepStatuses} />
          <Group direction="vertical" gap={20}>
            <Group direction="vertical">
              <Heading variant="heading6">Business details</Heading>
              <BodyText variant="medium">Tell us about your business</BodyText>
            </Group>
            <InputGrid>
              <TextInput
                label="Business name"
                defaultValue={businessName}
                maxLength={100}
                minLength={2}
                name="businessDetails.name"
                placeholder="Enter your business name"
                required
                requiredErrorMessage="Please enter a business name"
              />
              <TextInput
                label="ABN"
                minLength={11}
                name="businessDetails.businessIdentifier"
                pattern="^(\d *?){11}$"
                invalidErrorMessage="Please enter an 11 digit ABN "
                placeholder="Enter your ABN"
                required
                requiredErrorMessage="Please enter your ABN"
              />
              {/* When we have more than one country we need to allow this to be chosen */}
              <HiddenInput name="businessDetails.businessIdentifierType" value="australian_business_number"/>
              <Select
                label="Country"
                name="businessDetails.country"
                placeholder="e.g. Australia"
                required
                requiredErrorMessage="Please select a country"
              >
                {countryNames.map(
                  (countryName) =>
                    <option key={countryName} value={countryName}>{countryName}</option>,
                )}
              </Select>
              <Select
                label="Industry"
                name="businessDetails.industry"
                placeholder="e.g. Healthcare"
                required
                requiredErrorMessage="Please select an industry"
              >
                {BUSINESS_TRAVELLER_BUSINESS_INDUSTRIES_OPTIONS.map(
                  ([key, label]) =>
                    <option key={key} value={key}>{label}</option>,
                )}
              </Select>
              <Select
                label="Number of employees"
                name="businessDetails.numberOfEmployees"
                placeholder="e.g. 1 - 20"
                required
                requiredErrorMessage="Please select your number of employees"
              >
                {BUSINESS_TRAVELLER_BUSINESS_EMPLOYEE_COUNT_OPTIONS.map(
                  ([key, label]) =>
                    <option key={key} value={key}>{label}</option>,
                )}
              </Select>
            </InputGrid>
          </Group>
          <Hr/>
          <Group direction="vertical" gap={20}>
            <Group direction="vertical">
              <Heading variant="heading6">Contact details</Heading>
              <BodyText variant="medium">Provide a primary point of contact to ensure we can stay in touch</BodyText>
            </Group>
            <InputGrid>
              <TextInput
                autoComplete="given-name"
                label="First name"
                name="adminDetails.firstName"
                placeholder="Enter first name"
                required
                requiredErrorMessage="Please enter first name"
              />
              <TextInput
                autoComplete="family-name"
                label="Last name"
                name="adminDetails.lastName"
                placeholder="Enter last name"
                required
                requiredErrorMessage="Please enter last name"
              />
              <TextInput
                autoComplete="organization-title"
                label="Position"
                name="adminDetails.position"
                placeholder="Enter your position"
                required
                requiredErrorMessage="Please enter your position"
              />
              <PhoneInput
                defaultPhonePrefix="+61"
                label="Contact number"
                name="businessDetails.phone"
                placeholder="0400 000 000"
                required
                requiredErrorMessage="Number provided is incomplete. Please enter a 10-digit contact number"
              />
            </InputGrid>
          </Group>
          <Hr/>
          <Group direction="vertical" gap={20}>
            <TextInput
              autoComplete="referral-code"
              label="Referral code"
              name="businessDetails.referralCode"
              placeholder="Enter referral code"
              optionalIndicatorEnabled
            />
            <TextInput
              autoComplete="referral-email"
              label="Referrer's email address"
              name="businessDetails.referralEmail"
              placeholder="Enter referrer's email"
              optionalIndicatorEnabled
            />
          </Group>
          <Group direction="horizontal" horizontalAlign="end">
            <TextButton
              fit="mobile-full-width"
              kind="primary"
              shape="square"
              size="medium"
              type="submit"
              disabled={isFormSubmitting}
            >
              Create account
            </TextButton>
          </Group>
        </Group>
      </CreateBusinessForm>
    </SignupContainer>
  )
}

export default BusinessTravellerSignupCreateBusiness
